import { useState } from "react";
import type { WorkerHeatIndexSensorResponse } from "@safers/api";
import { HEAT_LEVEL_META, heatLevelOf, type HeatLevelMeta } from "@safers/ui";
import { formatNoticeTime } from "@/utils/date";

interface Props {
  sensors: WorkerHeatIndexSensorResponse[];
}

const VISIBLE_SENSOR_COUNT = 3;

function getApparentTemperature(sensor: WorkerHeatIndexSensorResponse): number | null {
  if (sensor.measuredAt == null) return null;
  return sensor.measurements.find((m) => m.field === "apparent_temperature")?.value ?? null;
}

function getHeatLevelMeta(temperature: number): HeatLevelMeta {
  return HEAT_LEVEL_META[heatLevelOf(temperature)];
}

/**
 * 현장 체감온도계 카드.
 * 측정이 끊긴 센서는 "측정 없음", 센서가 없는 현장은 카드를 그리지 않는다.
 */
export function HeatIndexSensorCard({ sensors }: Props) {
  const [expanded, setExpanded] = useState(false);
  if (sensors.length === 0) return null;

  const measuredSensors = sensors.map((sensor) => ({
    ...sensor,
    temperature: getApparentTemperature(sensor),
  }));
  const hiddenCount = measuredSensors.length - VISIBLE_SENSOR_COUNT;
  const visibleSensors = expanded
    ? measuredSensors
    : measuredSensors.slice(0, VISIBLE_SENSOR_COUNT);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="mb-1 text-sm font-semibold text-neutral-500">현장 체감온도</div>

      <ul className="divide-y divide-slate-100">
        {visibleSensors.map((sensor, index) => (
          <li key={`${index}-${sensor.name}`} className="flex items-center gap-2 py-2.5">
            <div>
              <div className="text-sm text-neutral-700">{sensor.name}</div>
              {sensor.measuredAt && (
                <div className="mt-0.5 text-xs text-neutral-400">
                  측정 {formatNoticeTime(sensor.measuredAt)}
                </div>
              )}
            </div>
            {sensor.temperature != null ? (
              <>
                <span
                  className="ml-auto text-base font-semibold"
                  style={{ color: getHeatLevelMeta(sensor.temperature).panelFg }}
                >
                  {sensor.temperature.toFixed(1)}°
                </span>
                <LevelChip meta={getHeatLevelMeta(sensor.temperature)} className="text-xs" />
              </>
            ) : (
              <span className="ml-auto text-sm text-neutral-400">측정 없음</span>
            )}
          </li>
        ))}
      </ul>
      {hiddenCount > 0 &&
        (expanded ? (
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="rounded-full border border-neutral-200 bg-white/95 px-2.5 py-1 text-xs font-semibold text-neutral-600 shadow-sm"
            >
              접기 ▲
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex h-11 w-full items-end justify-center pb-2 text-[0.8125rem] font-semibold text-neutral-600"
          >
            더 보기 ▼
          </button>
        ))}
    </section>
  );
}

function LevelChip({ meta, className }: { meta: HeatLevelMeta; className: string }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 font-bold ${className}`}
      style={{ backgroundColor: meta.chipBg, color: meta.chipFg }}
    >
      {meta.label}
    </span>
  );
}
